import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { useUserStore } from './stores';
import { ThemeProvider } from './contexts/ThemeContext';
import { initAnalytics, identifyUser, trackEvent, trackPageView } from './hooks/useAnalytics';
import ErrorBoundary from './components/ErrorBoundary';
import { ToastProvider } from './components/common/Toast';
import OfflineBanner from './components/common/OfflineBanner';
import GlobalHeader from './components/navigation/GlobalHeader';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import MobileHome from './pages/MobileHome';
import ItineraryView from './pages/ItineraryView';
import Settings from './pages/Settings';
import DeleteAccount from './pages/DeleteAccount';
import SharedItinerary from './pages/SharedItinerary';
import PackingPage from './pages/PackingPage';
import PlansV2 from './pages/PlansV2';
import PlanDetailV2 from './pages/PlanDetailV2';
import { creatorRoutes } from './creatorRoutes';
import DestinationPage from './pages/DestinationPage';
import { safeReturnTo } from './utils/returnTo';
import { useContextualLanding, useNextTripLanding } from './hooks/useContextualLanding';
import AppLoadingState from './components/common/AppLoadingState';
import DayDetailV2 from './pages/DayDetailV2';
import PlanTab from './pages/PlanTab';
import PodsScreen from './pages/PodsScreen';
import SimilarPlansView from './pages/SimilarPlansView';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import DeleteAccountRequest from './pages/DeleteAccountRequest';
import './App.css';

const LegacySlugRedirect: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { search } = useLocation();
  // Keep ?invite= and ?from= — dropping them loses the invitation token from
  // links already sent. (The server redirects these too; this path only runs
  // when the SPA is served directly, e.g. a static dev server.)
  return <Navigate to={`/plans/p/${slug}${search}`} replace />;
};

// Records a $pageview on every route change (must live inside BrowserRouter).
const RouteAnalytics: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
};


const AppContent: React.FC = () => {
  const user = useUserStore((state) => state.user);
  const userId = user?.id ?? null;

  // Contextual landing (spec §22.5, Phase 4): open on Now during a trip or within
  // seven days of departure, Explore otherwise. The decision is the server's; the
  // hook applies it at most once per session so the Now tab stays reachable, and
  // reports `holding` while an entry at `/` is still waiting for it, so Now is
  // never painted and then replaced. Signed-out visitors are left alone: they have
  // no trips, and bouncing a landing page would break every shared link.
  const landingAnswer = useNextTripLanding(userId);
  const { holding: landingHold } = useContextualLanding(landingAnswer.landing, {
    enabled: userId !== null,
    userId,
    failed: landingAnswer.failed,
  });

  return (
    <div className="app">
      <RouteAnalytics />
      <GlobalHeader />
      <Routes>
        {/* Today tab — works for both authed and unauthed */}
        <Route
          path="/"
          element={landingHold ? <AppLoadingState /> : <MobileHome user={user} />}
        />
        {/* Auth routes */}
        <Route
          path="/login"
          element={user ? <Navigate to={safeReturnTo()} replace /> : <Login />}
        />
        <Route
          path="/signup"
          element={user ? <Navigate to="/" /> : <Signup />}
        />
        <Route
          path="/reset-password/:uid/:token"
          element={user ? <Navigate to="/" /> : <ResetPassword />}
        />
        <Route
          path="/verify-email/:uid/:token"
          element={<VerifyEmail />}
        />
        {/* Itinerary routes */}
        <Route
          path="/itinerary/:id"
          element={user ? <ItineraryView user={user} /> : (
            window.location.search.includes('invite=')
              ? <ItineraryView user={null} />
              : <Navigate to={`/login?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`} />
          )}
        />
        <Route
          path="/plans/:id/similar"
          element={user ? <SimilarPlansView /> : <Navigate to="/login" />}
        />
        <Route
          path="/itinerary/:id/packing"
          element={user ? <PackingPage /> : <Navigate to="/login" />}
        />
        <Route
          path="/plan/new"
          element={user ? <Navigate to="/explore?chat=1" replace /> : <Navigate to="/login" />}
        />
        {/* Settings and Family */}
        <Route
          path="/family"
          element={user ? <Navigate to="/plans/you" replace /> : <Navigate to="/login" />}
        />
        <Route
          path="/settings"
          element={user ? <Settings /> : <Navigate to="/login" />}
        />
        <Route
          path="/settings/delete-account"
          element={user ? <DeleteAccount /> : <Navigate to="/login" />}
        />
        {/* Other routes */}
        <Route
          path="/chat"
          element={<Navigate to="/explore?chat=1" replace />}
        />
        {/* Public shared itinerary route (no auth required) */}
        <Route path="/shared/:token" element={<SharedItinerary />} />
        {/* Legal pages (public, no auth required) */}
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        {/* Public account-deletion page — the URL declared in Play Data safety.
            Must stay reachable signed out; /settings/delete-account cannot serve
            this purpose because it redirects to login. */}
        <Route path="/delete-account" element={<DeleteAccountRequest />} />
        {/* Four-tab navigation: Today (/), Explore (/explore), Plan (/plan), Pods (/pods) */}
        <Route path="/explore" element={<PlansV2 />} />
        <Route path="/plan" element={<PlanTab />} />
        <Route path="/pods" element={user ? <PodsScreen /> : <Navigate to="/login" />} />
        {/* Creator profile + collections (public) */}
        {creatorRoutes}
        {/* Destination pages — the atlas (spec §22.3). Public: the guide is
            the pitch, so it reads with no account. */}
        <Route path="/d/:slug" element={<DestinationPage />} />
        {/* Pinned edition: keeps rendering what was shared, after newer
            editions exist (§22.1). */}
        <Route path="/d/:slug/e/:edition" element={<DestinationPage />} />
        {/* Plan detail + day detail (public) */}
        <Route path="/plans/p/:slug" element={<PlanDetailV2 />} />
        <Route path="/plans/p/:slug/day/:dayNum" element={<DayDetailV2 />} />
        {/* Legacy route redirects */}
        <Route path="/plans" element={<Navigate to="/explore" replace />} />
        <Route path="/plans/you" element={<Navigate to="/plan" replace />} />
        <Route path="/plans/discover" element={<Navigate to="/explore" replace />} />
        <Route path="/plans/:slug" element={<LegacySlugRedirect />} />
        <Route
          path="/plans/trip/:id"
          element={user ? <ItineraryView user={user} /> : <Navigate to="/login" />}
        />
        {/* Redirect old routes to home */}
        <Route path="/weekly" element={<Navigate to="/" />} />
        <Route path="/monthly" element={<Navigate to="/" />} />
        <Route path="/dashboard" element={<Navigate to="/" />} />
        <Route path="/calendar" element={<Navigate to="/" />} />
        <Route path="/events" element={<Navigate to="/" />} />
        <Route path="/itineraries" element={<Navigate to="/" />} />
        <Route path="/setup" element={<Navigate to="/" />} />
        <Route path="/butler" element={<Navigate to="/" />} />
      </Routes>
    </div>
  );
};

const App: React.FC = () => {
  const isLoading = useUserStore((state) => state.isLoading);
  const user = useUserStore((state) => state.user);
  const fetchUser = useUserStore((state) => state.fetchUser);

  useEffect(() => {
    initAnalytics();
    // OAuth signup lands with ?welcome=1 (set server-side only on account
    // creation) — funnel step: plan → signup. Strip it after tracking.
    const params = new URLSearchParams(window.location.search);
    if (params.get('welcome') === '1') {
      trackEvent('signup_completed', { method: 'oauth' });
      params.delete('welcome');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    }
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    if (user) {
      identifyUser(user.id, { email: user.email, first_name: user.first_name });
    }
  }, [user]);

  if (isLoading) {
    return <AppLoadingState />;
  }

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <ToastProvider>
          <OfflineBanner />
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </ToastProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
